import type { ReactNode } from 'react';
import type { ContentTypePropertyConfig } from '../../../../lib/flotiq-client/types/api-response';

import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../../../lib/helpers';

import Button from '../../../../components/Button/Button';

import { ArrowUpIcon, DeleteIcon } from '../../../../images/shapes';

import { useFieldContext } from '../../../form';

interface ListFieldItemProps {
  /**
   * Label for the list item
   */
  label?: ReactNode;
  /**
   * List items properties
   */
  itemsProps: Exclude<ContentTypePropertyConfig['items'], undefined>;
  /**
   * Current value of the list field items
   */
  currentValue: any[];
  /**
   * Index of the current item
   */
  idx: number;
  /**
   * Function to render the list field
   */
  renderListField: (
    fieldName: string,
    itemName: string,
    idx: number,
  ) => ReactNode;
  /**
   * Function to handle moving the item up in the list
   */
  handleOrderUp: (idx: number) => void;
  /**
   * Function to handle moving the item down in the list
   */
  handleOrderDown: (idx: number) => void;
  /**
   * Function to handle deleting the item from the list
   */
  handleDeleteItem: (idx: number) => void;
  /**
   * If the form is disabled
   */
  disabled?: boolean;
  /**
   * If field is rendered in content type definition form
   */
  isCtdForm?: boolean;
  /**
   * Additional fields container classes
   */
  additionalClasses?: string;
  /**
   * Test ID for testing purposes
   */
  testId?: string;
}

const ListFieldItem = ({
  itemsProps,
  currentValue,
  idx,
  renderListField,
  handleOrderUp,
  handleOrderDown,
  handleDeleteItem,
  disabled,
  isCtdForm = false,
  testId,
  additionalClasses,
  label,
}: ListFieldItemProps) => {
  const field = useFieldContext();

  return (
    <div
      className="relative border bg-white dark:border-slate-800 rounded-lg mb-5
       dark:bg-slate-950"
    >
      <div
        className="w-full px-5 py-4 border-b border-slate-200 dark:border-slate-700 text-slate-400
        dark:text-slate-200"
      >
        {label} #{idx + 1}
      </div>
      <div
        className={twMerge(
          'bg-white dark:bg-slate-950 pt-2.5 md:pt-5 pb-2 md:pb-4 rounded-b-lg',
          additionalClasses,
        )}
      >
        {itemsProps.order.map((itemName) => {
          const fieldName = `${field.name}.${itemName}`;
          return (
            <div key={fieldName} className="px-2.5 md:px-5">
              {renderListField(fieldName, itemName, idx)}
            </div>
          );
        })}
      </div>
      <div className="absolute right-3 top-5 flex flex-row gap-2 md:gap-3">
        {!isCtdForm && currentValue.length > 1 && (
          <>
            <Button
              onClick={() => handleOrderUp(idx)}
              iconImage={
                <ArrowUpIcon
                  className={twMerge(
                    'h-3 md:h-4 text-slate-400',
                    !(disabled || idx === 0) && 'hover:text-slate-700',
                  )}
                />
              }
              buttonColor="borderless"
              additionalClasses="px-1"
              disabled={disabled || idx === 0}
              {...getTestProps(testId, 'up', 'testId')}
            />
            <Button
              onClick={() => handleOrderDown(idx)}
              iconImage={
                <ArrowUpIcon
                  className={twMerge(
                    'h-3 md:h-4 rotate-180 text-slate-400',
                    !(disabled || idx === currentValue.length - 1) &&
                      'hover:text-slate-700',
                  )}
                />
              }
              buttonColor="borderless"
              additionalClasses="px-1"
              disabled={disabled || idx === currentValue.length - 1}
              {...getTestProps(testId, 'down', 'testId')}
            />
          </>
        )}
        <Button
          onClick={() => handleDeleteItem(idx)}
          iconImage={
            <DeleteIcon
              className={twMerge(
                'h-3 md:h-4 text-slate-400',
                !disabled && 'hover:text-slate-700',
              )}
            />
          }
          buttonColor="borderless"
          additionalClasses="px-1"
          disabled={disabled}
          {...getTestProps(testId, 'delete', 'testId')}
        />
      </div>
    </div>
  );
};

export default ListFieldItem;
